import axios from 'axios';

import { useTokenStore } from '~entities/studios/model/token-store';
import { useUserTokenStore } from '~entities/users/model/token-store';

import { reportError } from '~shared/observability/report-error';

export enum ApiResponseStatus {
  SUCCESS = 'success',
  FAIL = 'fail',
}

// Expected client-side responses we don't want to treat as incidents:
// 401 (auth/redirect) and 422 (validation noise).
const IGNORED_STATUSES = new Set([401, 422]);

const shouldReport = (status: number | undefined) => {
  // Network errors (no response) and any 4xx/5xx that isn't expected noise.
  if (status === undefined) return true;
  return status >= 400 && !IGNORED_STATUSES.has(status);
};

// Create the API instance without initial headers
export const baseAPI = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

// Initialize token from persisted store
const initializeToken = () => {
  const token = useTokenStore.getState().token;
  if (token) {
    baseAPI.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  const userToken = useUserTokenStore.getState().token;
  if (userToken) {
    baseAPI.defaults.headers.common['X-User-Auth'] = `User ${userToken}`;
  }
};

// Set up axios interceptor to add token to requests
baseAPI.interceptors.request.use((config) => {
  const token = useTokenStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const userToken = useUserTokenStore.getState().token;
  if (userToken) {
    config.headers['X-User-Auth'] = `User ${userToken}`;
  }
  return config;
});

// Capture server/network failures centrally (skips expected 401/422 noise).
baseAPI.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (shouldReport(status)) {
        reportError(error, {
          featureArea: 'api',
          url: error.config?.url,
          method: error.config?.method,
          errorCode: status ? String(status) : 'network_error',
        });
      }
    }
    return Promise.reject(error);
  },
);

// Subscribe to token changes
useTokenStore.subscribe((state) => {
  const token = state.token;
  if (token) {
    baseAPI.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete baseAPI.defaults.headers.common.Authorization;
  }
});

// Subscribe to user token changes
useUserTokenStore.subscribe((state) => {
  const userToken = state.token;
  if (userToken) {
    baseAPI.defaults.headers.common['X-User-Auth'] = `User ${userToken}`;
  } else {
    delete baseAPI.defaults.headers.common['X-User-Auth'];
  }
});

// Initialize token when the API module loads
initializeToken();

// Optional: Create a hook to initialize API in components if needed
// export const useInitializeAPI = () => {
//   const token = useToken();

//   useEffect(() => {
//     if (token) {
//       baseAPI.defaults.headers.common.Authorization = `Bearer ${token}`;
//     }
//   }, [token]);
// };
